import React, { useEffect, useRef, useState } from 'react';
import { ArrowRightOutlined } from '@ant-design/icons';
import { getScrollParent } from '../AIEmployees/useReveal';
import './MobileStickyCta.css';

/**
 * MobileStickyCta — reusable mobile-only sticky bottom CTA bar for marketing
 * pages (the standing "all-pages mobile pattern"; first use: /ai-organization,
 * Aug 3 mobile pass). Desktop never sees it (display:none above 640px).
 *
 * Behavior (Aug 3 UX round — Nitish's screenshot review):
 *  1. Hidden until the visitor scrolls `afterPx` down, so it never covers the
 *     hero / landing view.
 *  2. Hidden while ANY inline primary CTA is on screen — pages mark those
 *     elements with `data-msc-suppress` — so the visitor never sees two
 *     "Hire" buttons at once (the sticky bar is a STAND-IN for the page's
 *     real CTAs, not a duplicate of them; same rule Amazon's sticky
 *     add-to-cart follows).
 *
 * Works in BOTH shells: the app shell scrolls `main.ant-layout-content`, the
 * marketing shell scrolls the document; the real scroll container is detected
 * from the component's own DOM position (same helper as useReveal — never
 * assume window scrolls).
 *
 * Render it at the END of the page content (it also renders a spacer so the
 * fixed bar never covers the page's last content/footer on mobile).
 */
export default function MobileStickyCta({ label, onClick, afterPx = 900, suppressSelector = '[data-msc-suppress]' }) {
  const [past, setPast] = useState(false);
  const [inlineCtaOnScreen, setInlineCtaOnScreen] = useState(false);
  const anchorRef = useRef(null);

  useEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor) return undefined;
    const parent = getScrollParent(anchor);
    const target = parent || window;
    const read = () => (parent ? parent.scrollTop : window.scrollY || document.documentElement.scrollTop || 0);
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        // Hysteresis: show past afterPx, but only hide again once the visitor
        // is clearly ABOVE it (afterPx - 160). A single threshold made the bar
        // pop in/out repeatedly when a scroll settled right on the line
        // (Nitish's Aug 4 video: bar flickering near the top of the page).
        const y = read();
        setPast((prev) => (prev ? y > afterPx - 160 : y > afterPx));
      });
    };
    onScroll();
    target.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      target.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [afterPx]);

  // Suppression: watch every inline CTA the page marked; while one is visible
  // (any pixel), the bar stays down. Viewport-root IntersectionObserver is
  // correct in both shells — visibility is a viewport question regardless of
  // which container scrolls.
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const els = Array.from(document.querySelectorAll(suppressSelector));
    if (!els.length) return undefined;
    const visible = new Set();
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting) visible.add(en.target);
        else visible.delete(en.target);
      });
      setInlineCtaOnScreen(visible.size > 0);
    }, {
      // Look 120px beyond the viewport: the bar starts hiding BEFORE the
      // inline CTA scrolls in and stays down until it's clearly gone — the
      // visitor never sees both buttons at once or a same-instant swap
      // (the abrupt pop in Nitish's Aug 4 video).
      rootMargin: '120px 0px 120px 0px',
    });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [suppressSelector]);

  const show = past && !inlineCtaOnScreen;
  const barRef = useRef(null);

  // CellCog's own support chat launcher sits bottom right; while this bar shows it rises above it (Oct 28 2026: the
  // launcher covered "Get started" on phones). Only when the chat loaded; desktop's bar is display:none, so 0 there.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.CellCog !== 'function') return undefined;
    window.CellCog('offset', show && barRef.current ? barRef.current.offsetHeight : 0);
    return () => { if (typeof window.CellCog === 'function') window.CellCog('offset', 0); };
  }, [show]);

  return (
    <>
      <span ref={anchorRef} className="msc-anchor" aria-hidden="true" />
      <span className="msc-spacer" aria-hidden="true" />
      <div ref={barRef} className={`msc-bar${show ? ' is-show' : ''}`} aria-hidden={!show}>
        <button
          type="button"
          className="aie-btn aie-btn-primary msc-bar__btn"
          onClick={onClick}
          tabIndex={show ? 0 : -1}
        >
          {label} <ArrowRightOutlined />
        </button>
      </div>
    </>
  );
}
